import { useCallback, useEffect, useMemo, useState } from 'react';
import { describeAbsence } from '@/api/absenceOrders';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import {
  departmentsApi, employees, individuals, positionsApi, workSchedulesApi,
  PSP_CATEGORY_LABELS, PSP_CHILDREN_CATEGORIES, EMPLOYMENT_TYPE_LABELS,
  EMPLOYMENT_KIND_LABELS,
  type Individual, type PspCategory, type EmploymentType, type EmploymentKind,
} from '@/api/hr';
import { orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { vacationOrder } from '@/api/vacation';
import { useConfirm } from '@/components/ConfirmProvider';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import AccountSelect from '@/components/AccountSelect';
import EmployeeDocumentsSection from './EmployeeDocumentsSection';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocBlock from '@/shared/components/DocBlock';
import DocLayout from '@/shared/components/DocLayout';
import DocRail from '@/shared/components/DocRail';
import EmployeePlannedAccrualsSection from './EmployeePlannedAccrualsSection';
import DatePopover from '@/components/DatePopover';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { withLegacyForm } from '@/shared/hooks/draftSlots';
import TchTable from '@/shared/components/TchTable';

interface Form {
  organization_id: string;
  individual_id: string;
  code: string;
  employment_kind: EmploymentKind;
  employment_type: EmploymentType;
  psp_category: PspCategory | null;
  children_count: number | null;
  // Фіча картки #2: персональні рахунки обліку оплат (override картки).
  expense_account_id: string;
  payroll_account_id: string;
  // Графік роботи (для авто-заповнення табеля).
  work_schedule_id: string;
  // ч.806: місце діяльності (відокремлений підрозділ); '' = основне місце.
  activity_place_id: string;
  // V1: норма днів відпустки + стартовий залишок.
  vacation_days_per_year: number | null;
  vacation_additional_days_per_year: number | null;
  vacation_balance_start_days: number | null;
  vacation_balance_start_date: string;
  comment: string;  // ч.883
  // ч.1208: ознаки для нормативу працевлаштування осіб з інвалідністю.
  excluded_from_disability_norm: boolean;
}

// Форма для нового физлица — отображается только когда выбрано "+ створити нову".
interface NewInd {
  last_name: string;
  first_name: string;
  middle_name: string;
  inn: string;
  birth_date: string;
  phone: string;
  email: string;
}

const EMPTY_FORM: Form = {
  organization_id: '',
  individual_id: '',
  code: '',
  employment_kind: 'MAIN',
  employment_type: 'STAFF',
  psp_category: null,
  children_count: null,
  expense_account_id: '',
  payroll_account_id: '',
  work_schedule_id: '',
  activity_place_id: '',
  vacation_days_per_year: 24,
  vacation_additional_days_per_year: 0,
  vacation_balance_start_days: null,
  vacation_balance_start_date: '',
  comment: '',
  excluded_from_disability_norm: false,
};

// Block 9 (2026-05-27): ПСП dropdown options (з SPEC §7.1).
// TD-B-P5.4.2 (2026-06-01) додав HERO_OF_UKRAINE, STUDENT, WIDOW_WITH_CHILDREN
// у PSP_CATEGORY_LABELS — генеруємо options з labels щоб не дублювати.
// Sentinel: Block9PspUi.test.tsx перевіряє що ВСI 12 категорій рендеряться.
// Empty value '' = «Без права на ПСП» (psp_category=null).
const PSP_OPTIONS: ReadonlyArray<{ value: '' | PspCategory; label: string }> = [
  { value: '', label: 'Без права на ПСП' },
  ...(Object.entries(PSP_CATEGORY_LABELS) as Array<[PspCategory, string]>).map(
    ([value, label]) => ({ value, label }),
  ),
];

const EMPTY_IND: NewInd = {
  last_name: '', first_name: '', middle_name: '',
  inn: '', birth_date: '', phone: '', email: '',
};

const fmt2 = (v: string | number | null) =>
  v == null ? '—' : Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function EmployeeEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // Випадашки «створити наказ» і «друк» (рішення власника 2026-08-19).
  // UX β: фільтр org dropdown по payroll.manage permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('payroll.manage');
  const empQuery = useQuery({
    queryKey: ['employee-full', id],
    queryFn: () => employees.get(id!),
    enabled: !isNew,
  });
  const historyQ = useQuery({
    queryKey: ['employee-history', id],
    queryFn: () => employees.history(id!),
    enabled: !isNew,
  });
  const indListQ = useQuery({
    queryKey: ['individuals'],
    queryFn: () => individuals.list({ limit: 1000 }),
  });
  const posQ = useQuery({
    queryKey: ['positions'],
    queryFn: () => positionsApi.list({ limit: 500 }),
  });
  const schedQ = useQuery({
    queryKey: ['work-schedules'],
    queryFn: () => workSchedulesApi.list(),
  });
  const depQ = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentsApi.list({ limit: 500 }),
  });

  const [form, setForm] = useState<Form>(EMPTY_FORM);

  // V3: залишок днів відпустки (для вкладки «Відпустка»). Після form (використовує його).
  const vacBalanceQ = useQuery({
    queryKey: ['employee-vac-balance', id, form.organization_id],
    queryFn: () => vacationOrder.employeeBalance(id!, form.organization_id),
    enabled: !isNew && !!form.organization_id,
  });
  // Прев'ю ефективних рахунків (резолвер, Закон #6) — щоб у полях override
  // показувати КОНКРЕТНИЙ рахунок, що спрацює, замість «з правил» (як у картці
  // номенклатури). Placeholder = резолвнутий код + джерело.
  const accPreviewQ = useQuery({
    queryKey: ['payroll-acc-preview', form.organization_id, id],
    queryFn: () => refs.payrollAccountRules.preview({
      organization_id: form.organization_id, employee_id: id!,
    }),
    enabled: !isNew && !!form.organization_id && !!id,
    refetchOnMount: true,
  });
  // Текст-підказка для поля override: «<код> (<джерело>)» або дефолт, поки немає прев'ю.
  const accPlaceholder = (key: 'expense' | 'payroll', fallback: string): string => {
    const it = accPreviewQ.data?.[key];
    if (!it?.account_code) return fallback;
    const src = it.source === 'card' ? 'поле картки'
      : it.source === 'rule' ? 'з правил'
      : it.source === 'civil_default' ? 'стандарт ЦПУ' : 'стандарт';
    return `${it.account_code} (${src})`;
  };
  const [createNew, setCreateNew] = useState(false);
  const [newInd, setNewInd] = useState<NewInd>(EMPTY_IND);
  const [error, setError] = useState<string | null>(null);

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  // ч.1060: разом з формою — і дані НОВОЇ фізособи, заповнені тут же
  // (раніше вони у чернетку не потрапляли й губились при виході).
  const draftValue = useMemo(() => ({ form, createNew, newInd }), [form, createNew, newInd]);
  const restoreDraft = useCallback((raw: unknown) => {
    // Чернетки до ч.1060 зберігали лише форму — розбираємо обидва формати.
    const v = withLegacyForm<{ form: Form; createNew?: boolean; newInd?: NewInd }>(raw);
    if (v.form) setForm(v.form);
    setCreateNew(!!v.createNew);
    setNewInd({ ...EMPTY_IND, ...(v.newInd ?? {}) });
  }, []);
  useFormDraft(draftValue, restoreDraft, { ready: isNew || !!empQuery.data });
  // Активна вкладка картки (склад вкладок залежить від виду зайнятості — нижче).
  const [tab, setTab] = useState<string>('history');

  useEffect(() => {
    if (isNew) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : { ...f, organization_id: firstOrg });
    } else if (empQuery.data) {
      const e = empQuery.data;
      setForm({
        organization_id: e.organization_id,
        individual_id: e.individual_id,
        code: e.code ?? '',
        employment_kind: e.employment_kind,
        employment_type: e.employment_type,
        psp_category: e.psp_category,
        children_count: e.children_count,
        expense_account_id: e.expense_account_id ?? '',
        payroll_account_id: e.payroll_account_id ?? '',
        work_schedule_id: e.work_schedule_id ?? '',
        activity_place_id: e.activity_place_id ?? '',
        vacation_days_per_year: e.vacation_days_per_year ?? 24,
        vacation_additional_days_per_year: e.vacation_additional_days_per_year ?? 0,
        vacation_balance_start_days: e.vacation_balance_start_days ?? null,
        vacation_balance_start_date: e.vacation_balance_start_date ?? '',
        comment: e.comment ?? '',
        excluded_from_disability_norm: e.excluded_from_disability_norm ?? false,
      });
    }
  }, [isNew, empQuery.data, availableOrgs]);

  const createInd = useMutation({
    mutationFn: (p: NewInd) => individuals.create({
      last_name: p.last_name,
      first_name: p.first_name,
      middle_name: p.middle_name || null,
      inn: p.inn || null,
      birth_date: p.birth_date || null,
      phone: p.phone || null,
      email: p.email || null,
    }),
  });

  const createEmp = useMutation({
    mutationFn: async () => {
      let indId = form.individual_id;
      if (createNew) {
        if (!newInd.last_name || !newInd.first_name) {
          throw new Error('Прізвище та ім\'я обов\'язкові');
        }
        const ind = await createInd.mutateAsync(newInd);
        indId = ind.id;
        await qc.invalidateQueries({ queryKey: ['individuals'] });
      }
      if (!indId) throw new Error('Оберіть фізособу');
      return employees.create({
        organization_id: form.organization_id,
        individual_id: indId,
        code: form.code || null,
        employment_kind: form.employment_kind,
        employment_type: form.employment_type,
        psp_category: form.psp_category,
        children_count: form.children_count,
        activity_place_id: form.activity_place_id || null,
      });
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['employees-full'] });
      navigate(`/employees/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });

  const updateEmp = useMutation({
    mutationFn: () => employees.update(id!, {
      code: form.code,
      employment_kind: form.employment_kind,
      employment_type: form.employment_type,
      psp_category: form.psp_category,
      children_count: form.children_count,
      expense_account_id: form.expense_account_id || null,
      payroll_account_id: form.payroll_account_id || null,
      work_schedule_id: form.work_schedule_id || null,
      activity_place_id: form.activity_place_id || null,
      vacation_days_per_year: form.vacation_days_per_year,
      vacation_additional_days_per_year: form.vacation_additional_days_per_year,
      vacation_balance_start_days: form.vacation_balance_start_days,
      vacation_balance_start_date: form.vacation_balance_start_date || null,
      comment: form.comment.trim() || null,
      excluded_from_disability_norm: !!form.excluded_from_disability_norm,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employee-full', id] });
      qc.invalidateQueries({ queryKey: ['payroll-acc-preview'] });
      // Стартовий залишок → OPENING-рух у реєстрі: перечитати залишок відпустки.
      qc.invalidateQueries({ queryKey: ['employee-vac-balance', id] });
    },
    onError: (e) => setError(extractError(e)),
  });

  const removeEmp = useMutation({
    mutationFn: () => employees.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['employees-full'] });
      navigate('/employees');
    },
    onError: (e) => setError(extractError(e)),
  });

  const emp = empQuery.data;
  const isWorking = emp?.hire_date != null && emp?.dismiss_date == null;
  const isDismissed = emp?.dismiss_date != null;
  // Картка блокується щойно з'являється наказ прийому (авто-створюється з
  // співробітником). Рахунки обліку оплат лишаються редагованими (запит власника
  // 2026-06-15). Зміни параметрів — через наказ (поки не проведено) / переміщення.
  const locked = !isNew && emp?.hire_order_id != null;
  const indOptions = indListQ.data?.items ?? [];
  const posMap = new Map((posQ.data?.items ?? []).map((p) => [p.id, p.name]));
  const depMap = new Map((depQ.data?.items ?? []).map((d) => [d.id, d.name]));

  // UX β: warning якщо новий документ а user не має payroll.manage у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: 'var(--badge-amber-bg)', borderRadius: 6,
            border: '1px solid #fbbf24', color: 'var(--badge-amber-fg)',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати співробітника у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/employees')}>Скасувати</button>
        </div>
      </div>
    );
  }

  // ч.885 (запит власника): «ще дії» → «Повʼязані дії» правої колонки +
  // «Історія документа» (довідник — без звʼязаних, ч.809).
  const railActions = [
      { label: 'наказ: кадрове переміщення',
        onClick: () => navigate(`/staff-transfers/new?employee=${id}`) },
      { label: 'наказ: звільнення',
        onClick: () => navigate(`/dismissal-orders/new?employee=${id}`) },
      { label: 'наказ: відпустка',
        onClick: () => navigate(`/hr/vacation-orders/new?employee=${id}&org=${form.organization_id}`) },
      { label: 'наказ: зміна нарахувань',
        onClick: () => navigate(`/payroll/planned-accrual-orders/new?employee=${id}&org=${form.organization_id}`) },
      { label: 'друк: довідка з місця роботи',
        onClick: () => navigate(`/employees/${id}/print?form=work`) },
      { label: 'друк: довідка про доходи',
        onClick: () => navigate(`/employees/${id}/print?form=income`) },
    ].map((a) => (isNew
      ? { ...a, disabled: true, title: 'Спершу збережіть картку працівника' }
      : a));

  return (
    <div className="card">
      <div className="toolbar with-filters">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Новий співробітник' :
           emp ? `${emp.full_name ?? '—'}${emp.code ? ` (таб. ${emp.code})` : ''}` :
           '...'}
        </h2>
        {/* ч.642: канон DocumentToolbar — плашка дій + бейджі стану в рядку назви. */}
        <DocumentToolbar
          onBackToList={() => navigate('/employees')}
          onSave={() => (isNew ? createEmp.mutate() : updateEmp.mutate())}
          savePending={createEmp.isPending || updateEmp.isPending}
          postable={false}
          isNew={isNew}
          deletable={!isWorking}
          onDelete={() => { confirm('Видалити картку?').then((ok) => { if (ok) removeEmp.mutate(); }); }}
          // ч.809: довідник — лише «історія» (аудит-тригер 0499), без дт-кт/звʼязаних.
          historyDoc={isNew ? undefined : { type: 'ref.employees', id: id! }}
          extraStatusBadges={[
            ...(isWorking ? [{ label: 'працює', tone: 'green' as const }] : []),
            ...(isDismissed ? [{ label: 'звільнений', tone: 'red' as const }] : []),
          ]}
          // ч.702: дії видно завжди; на новій картці — disabled з підказкою.
          extraActions={railActions}
          railMode
        />
      </div>
      <DocLayout rail={
        <DocRail actions={railActions} isNew={isNew} related={false}
          doc={isNew ? undefined : { type: 'ref.employees', id: id! }} />
      }>
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Організація</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Табельний №</label>
          {/* Для ЦПУ табельний № не редагується (запит власника 2026-06-15). */}
          <input value={form.code}
                 onChange={(e) => setForm({ ...form, code: e.target.value })}
                 disabled={locked || (!isNew && form.employment_type === 'CIVIL_LAW')} />
        </div>
        {/* Вид зайнятості (тип договору) — для CIVIL_LAW приховано (запит власника
            2026-06-15): у картці ЦПУ цей вибір не показуємо. При створенні (isNew)
            показуємо завжди — треба обрати тип. */}
        {(isNew || form.employment_type !== 'CIVIL_LAW') && (
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label>Вид зайнятості</label>
            <select value={form.employment_type}
                    disabled={locked}
                    onChange={(e) => setForm({
                      ...form, employment_type: e.target.value as EmploymentType,
                    })}>
              {(Object.entries(EMPLOYMENT_TYPE_LABELS) as Array<[EmploymentType, string]>)
                .map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
            </select>
            {form.employment_type === 'NO_CONTRACT' && (
              <small style={{ color: 'var(--badge-amber-fg)' }}>
                Інформаційний вид — без нарахувань, ЄСВ/ПДФО та звітності.
              </small>
            )}
            {form.employment_type === 'CIVIL_LAW' && (
              <small style={{ color: '#555' }}>
                Без ПСП, відпусток і лікарняних. Підстава — договір ЦПУ.
              </small>
            )}
          </div>
        )}
        {/* «Форма зайнятості» — для CIVIL_LAW приховано (запит власника 2026-06-15).
            При створенні (isNew) показуємо завжди. */}
        {(isNew || form.employment_type !== 'CIVIL_LAW') && (
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label>Форма зайнятості</label>
            <select value={form.employment_kind}
                    disabled={locked}
                    onChange={(e) => setForm({ ...form, employment_kind: e.target.value as EmploymentKind })}>
              {(Object.entries(EMPLOYMENT_KIND_LABELS) as Array<[EmploymentKind, string]>)
                .map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        )}
        {/* Графік роботи — нагорі картки (для авто-заповнення табеля). */}
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Графік роботи</label>
          <select value={form.work_schedule_id}
                  disabled={locked}
                  onChange={(e) => setForm({ ...form, work_schedule_id: e.target.value })}>
            <option value="">— за замовчуванням —</option>
            {(schedQ.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
        {/* ч.806: місце діяльності (відокремлений підрозділ) — лише юрособи з
            місцями у картці організації (вкладка «Податкові рахунки»). ПДФО
            працівника місця — до місцевого бюджету місця + окремий 4ДФ. */}
        {(() => {
          const org = orgList.data?.items.find((o) => o.id === form.organization_id);
          const places = org?.entity_type === 'LEGAL_ENTITY' ? (org.activity_places ?? []) : [];
          if (!places.length) return null;
          return (
            <div className="field" style={{ gridColumn: 'span 4' }}>
              <label>Місце діяльності</label>
              <select value={form.activity_place_id} disabled={locked}
                      title="Відокремлений підрозділ: ПДФО — до місцевого бюджету місця, працівник — у додатковому розрахунку 4ДФ за підрозділ"
                      onChange={(e) => setForm({ ...form, activity_place_id: e.target.value })}>
                <option value="">основне місце (організація)</option>
                {places.map((p) => (
                  <option key={p.id ?? p.name} value={p.id ?? ''}>{p.name}{p.katottg ? ` · ${p.katottg}` : ''}</option>
                ))}
              </select>
            </div>
          );
        })()}
      </div>

      {/* ч.1208–1209: облікова чисельність — позначка «шкідливі умови» (картка) і чинна
          тривала відсутність (проведений наказ: догляд за дитиною / військова служба /
          призупинення договору). Не залежать від наказу про прийом. */}
      {!isNew && form.employment_type !== 'CIVIL_LAW' && (
        <>
          <h3 style={{ margin: '1rem 0 0.5rem' }}>Облікова чисельність</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr', gap: '0.75rem', alignItems: 'center' }}>
            <div data-testid="employee-absence" style={{ fontSize: '0.9rem' }}>
              {empQuery.data?.absence_kind ? (
                <>
                  <span style={{ color: 'var(--badge-amber-fg)', fontWeight: 600 }}>
                    Зараз: {describeAbsence(empQuery.data.absence_kind, empQuery.data.absence_from ?? '', empQuery.data.absence_to)}
                  </span>{' '}
                  <a style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/absence-orders/${empQuery.data!.absence_order_id}`)}>наказ ↗</a>
                </>
              ) : (
                <>
                  <span style={{ color: 'var(--text-secondary)' }}>Тривалої відсутності немає.</span>{' '}
                  <a style={{ cursor: 'pointer' }}
                    title="Відпустка для догляду за дитиною, військова служба або призупинення трудового договору"
                    onClick={() => navigate(`/absence-orders/new?employee=${id}&org=${form.organization_id}`)}>
                    оформити наказ ↗
                  </a>
                </>
              )}
            </div>
            <div className="field" style={{ margin: 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}
                title="Роботи з важкими, шкідливими чи небезпечними умовами праці за урядовим переліком: така посада не входить у чисельність для нормативу працевлаштування осіб з інвалідністю, а її зарплата — у розрахунок внеску">
                <input type="checkbox" checked={!!form.excluded_from_disability_norm} style={{ width: 'auto' }}
                  onChange={(e) => setForm({ ...form, excluded_from_disability_norm: e.target.checked })} />
                Шкідливі умови праці (поза нормативом осіб з інвалідністю)
              </label>
            </div>
          </div>
        </>
      )}

      {/* Виплата ЗП Фаза 2: реквізити виплати (дефолт для відомості виплати). */}
      {/* Реквізити для виплат — read-only з картки ФIЗОСОБИ (джерело істини,
          alembic 0314). Редагуються лише на картці фізособи (кнопка «Створити/
          Редагувати»). Спосіб виводиться: є IBAN → банк, інакше → каса. */}
      <h3 style={{ margin: '1rem 0 0.5rem', display: 'flex', alignItems: 'baseline', gap: '0.6rem' }}>
        Реквізити для виплат
        {!isNew && empQuery.data?.individual_id && (
          <button type="button"
                  onClick={() => navigate(`/individuals/${empQuery.data!.individual_id}`)}
                  style={{
                    border: 'none', background: 'none', cursor: 'pointer', padding: 0,
                    fontSize: '0.82rem', fontWeight: 'normal', color: 'var(--primary)',
                  }}>
            {empQuery.data?.individual_payout_iban ? 'редагувати ↗' : 'створити ↗'}
          </button>
        )}
      </h3>
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Спосіб виплати</label>
          <input value={empQuery.data?.individual_payout_iban ? 'Банк (карта/рахунок)' : 'Готівка (каса)'}
                 readOnly tabIndex={-1}
                 style={{ background: 'var(--bg-soft)', color: 'var(--text-secondary)' }} />
        </div>
        <div className="field" style={{ gridColumn: 'span 5' }}>
          <label>IBAN</label>
          <input value={empQuery.data?.individual_payout_iban ?? '—'}
                 readOnly tabIndex={-1}
                 style={{ background: 'var(--bg-soft)', color: 'var(--text-secondary)' }} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Банк отримувача</label>
          <input value={empQuery.data?.individual_payout_bank_name ?? '—'}
                 readOnly tabIndex={-1}
                 style={{ background: 'var(--bg-soft)', color: 'var(--text-secondary)' }} />
        </div>
      </div>

      {/* Блок «Податкова соціальна пільга (ПСП)» прибрано з картки повністю
          (запит власника 2026-06-15). ПСП налаштовується при нарахуванні ЗП
          (PayrollEditor). psp_category/children_count лишаються у формі (зберігається
          дефолт/міграційне значення), просто не редагуються тут. */}

      {isNew && (
        <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'var(--bg-soft-2)',
                      borderRadius: 6 }}>
          <label style={{ fontWeight: 600 }}>Фізособа</label>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.3rem' }}>
            <label>
              <input type="radio" checked={!createNew}
                     onChange={() => setCreateNew(false)} />
              &nbsp;Обрати існуючу
            </label>
            <label>
              <input type="radio" checked={createNew}
                     onChange={() => setCreateNew(true)} />
              &nbsp;Створити нову
            </label>
          </div>
          {!createNew ? (
            <select value={form.individual_id}
                    onChange={(e) => setForm({ ...form, individual_id: e.target.value })}
                    style={{ width: '100%', marginTop: '0.4rem' }}>
              <option value="">— оберіть —</option>
              {indOptions.map((i: Individual) => (
                <option key={i.id} value={i.id}>
                  {i.full_name}{i.inn ? ` (ІПН ${i.inn})` : ''}
                </option>
              ))}
            </select>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem',
                          marginTop: '0.4rem' }}>
              <div className="field">
                <label>Прізвище *</label>
                <input value={newInd.last_name}
                       onChange={(e) => setNewInd({ ...newInd, last_name: e.target.value })} />
              </div>
              <div className="field">
                <label>Ім'я *</label>
                <input value={newInd.first_name}
                       onChange={(e) => setNewInd({ ...newInd, first_name: e.target.value })} />
              </div>
              <div className="field">
                <label>По батькові</label>
                <input value={newInd.middle_name}
                       onChange={(e) => setNewInd({ ...newInd, middle_name: e.target.value })} />
              </div>
              <div className="field">
                <label>ІПН</label>
                <input value={newInd.inn} maxLength={12}
                       onChange={(e) => setNewInd({ ...newInd, inn: e.target.value })} />
              </div>
              <div className="field">
                <label>Дата народження</label>
                <DatePopover value={newInd.birth_date}
                       onChange={(v) => setNewInd({ ...newInd, birth_date: v })} />
              </div>
              <div className="field">
                <label>Телефон</label>
                <input value={newInd.phone}
                       onChange={(e) => setNewInd({ ...newInd, phone: e.target.value })} />
              </div>
              <div className="field" style={{ gridColumn: 'span 3' }}>
                <label>Email</label>
                <input value={newInd.email}
                       onChange={(e) => setNewInd({ ...newInd, email: e.target.value })} />
              </div>
            </div>
          )}
        </div>
      )}

      {emp && (() => {
        const isCivil = form.employment_type === 'CIVIL_LAW';
        // Склад вкладок залежить від виду зайнятості (запит власника 2026-06-15):
        //  - ЦПУ: лише «Договори ЦПУ» + «Рахунки обліку оплат»;
        //  - трудовий/інше: Кадрова історія / Планові нарахування / Кадрові
        //    документи / Рахунки обліку оплат.
        // Перша вкладка «Дані фізособи» — не контент, а перехід на картку
        // фізособи у розділі Фізособи (запит власника). Решта — за видом зайнятості.
        const tabs: Array<[string, string]> = isCivil
          ? [['personal', 'Дані фізособи'], ['contracts', 'Договори ЦПУ'],
             ['accounts', 'Рахунки обліку оплат']]
          : [['personal', 'Дані фізособи'], ['history', 'Кадрова історія'],
             ['accruals', 'Планові нарахування'], ['documents', 'Кадрові документи'],
             ['vacation', 'Відпустка'],
             ['accounts', 'Рахунки обліку оплат']];
        const goPersonal = () => {
          if (form.individual_id) navigate(`/individuals/${form.individual_id}`);
        };
        const cur = tabs.some(([k]) => k === tab) ? tab : (isCivil ? 'contracts' : 'history');
        return (
        <>
          {/* Шапка: поточний стан (read-only). */}
          {/* «Поточний стан» — лише для трудового. Для ЦПУ приховано (запит
              власника 2026-06-15): у ЦПУ немає посади/окладу/дат прийому. */}
          {!isCivil && (
            <>
              <h3 style={{ marginTop: '1rem' }}>Поточний стан</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem',
                            padding: '0.5rem', background: 'var(--bg-soft)', borderRadius: 6 }}>
                <div><strong>Посада:</strong> {emp.position_name ?? '—'}</div>
                <div><strong>Підрозділ:</strong> {emp.department_name ?? '—'}</div>
                <div><strong>Оклад:</strong> {fmt2(emp.salary_amount)} грн</div>
                <div><strong>Прийнято:</strong> {emp.hire_date ?
                  new Date(emp.hire_date).toLocaleDateString('uk-UA') : '—'}</div>
                <div><strong>Звільнено:</strong> {emp.dismiss_date ?
                  new Date(emp.dismiss_date).toLocaleDateString('uk-UA') : '—'}</div>
                {emp.dismiss_reason && (
                  <div><strong>Стаття:</strong> {emp.dismiss_reason}</div>
                )}
              </div>
            </>
          )}

          {/* Таб-бар (сегментований стиль .seg-tabs, як у картці Фізособи). */}
          <div className="seg-tabs">
            {tabs.map(([key, label]) => (
              <button key={key} type="button"
                onClick={() => (key === 'personal' ? goPersonal() : setTab(key))}
                className={cur === key ? 'active' : ''}>
                {label}
              </button>
            ))}
          </div>

          {cur === 'accounts' && (
            <>
              <div className="doc-grid">
                <div className="field" style={{ gridColumn: 'span 6' }}>
                  <label>Рахунок витрат (override)</label>
                  <AccountSelect
                    value={form.expense_account_id}
                    onChange={(v) => setForm({ ...form, expense_account_id: v })}
                    placeholder={accPlaceholder('expense', '— з правил/стандарт —')}
                  />
                </div>
                <div className="field" style={{ gridColumn: 'span 6' }}>
                  <label>Рахунок розрахунків (override)</label>
                  <AccountSelect
                    value={form.payroll_account_id}
                    onChange={(v) => setForm({ ...form, payroll_account_id: v })}
                    placeholder={accPlaceholder('payroll', '— з правил/стандарт —')}
                  />
                </div>
              </div>
            </>
          )}

          {cur === 'vacation' && (
            <>
              {/* Залишок днів (read-only, з реєстру). */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem',
                            padding: '0.6rem', background: '#ecfdf5', borderRadius: 6,
                            marginBottom: '0.75rem' }}>
                <div><strong>Залишок основної відпустки:</strong>{' '}
                  {vacBalanceQ.data ? `${fmt2(vacBalanceQ.data.annual_basic_balance)} дн.` : '—'}</div>
                <div><strong>Залишок додаткової:</strong>{' '}
                  {vacBalanceQ.data ? `${fmt2(vacBalanceQ.data.annual_additional_balance)} дн.` : '—'}</div>
              </div>
              <div className="doc-grid">
                <div className="field" style={{ gridColumn: 'span 6' }}>
                  <label>Норма основної відпустки (днів/рік)</label>
                  <input type="number" min={0} max={365}
                    value={form.vacation_days_per_year ?? ''}
                    onChange={(e) => setForm({ ...form,
                      vacation_days_per_year: e.target.value === '' ? null : Number(e.target.value) })} />
                </div>
                <div className="field" style={{ gridColumn: 'span 6' }}>
                  <label>Норма додаткової (днів/рік)</label>
                  <input type="number" min={0} max={365}
                    value={form.vacation_additional_days_per_year ?? ''}
                    onChange={(e) => setForm({ ...form,
                      vacation_additional_days_per_year: e.target.value === '' ? null : Number(e.target.value) })} />
                </div>
                <div className="field" style={{ gridColumn: 'span 6' }}>
                  <label>
                    Стартовий залишок (днів)
                    {empQuery.data?.vacation_balance_is_estimated && (
                      <span title="Розраховано на основі імпортованих відомостей з 1С — потребує перевірки. Збережіть вручну, щоб підтвердити."
                        style={{ color: 'var(--badge-amber-fg)', marginLeft: 6, cursor: 'help' }}>⚠</span>
                    )}
                  </label>
                  <input type="number" min={0} step="0.01"
                    value={form.vacation_balance_start_days ?? ''}
                    onChange={(e) => setForm({ ...form,
                      vacation_balance_start_days: e.target.value === '' ? null : Number(e.target.value) })} />
                  {empQuery.data?.vacation_balance_is_estimated && (
                    <small style={{ color: 'var(--badge-amber-fg)' }}>
                      ⚠ Розраховано з імпорту 1С — перевірте і збережіть.
                    </small>
                  )}
                </div>
                <div className="field" style={{ gridColumn: 'span 6' }}>
                  <label>Залишок на дату</label>
                  <DatePopover value={form.vacation_balance_start_date}
                    onChange={(v) => setForm({ ...form, vacation_balance_start_date: v })} />
                </div>
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                Стартовий залишок записується у реєстр як OPENING-рух. Щомісячне накопичення
                (норма/12) виконується автоматично при «Закритті місяця» (додатково нічого
                робити не треба; кнопка на сторінці виплат — резервна для ручного донарахування).
              </p>
            </>
          )}

          {cur === 'history' && (
            (historyQ.data ?? []).length === 0 ? (
              <p style={{ color: 'var(--text-secondary)' }}>Записів немає. Оформіть «Прийом на роботу».</p>
            ) : (
              <TchTable>
                <thead>
                  <tr>
                    <th>Дата</th>
                    <th>Посада</th>
                    <th>Підрозділ</th>
                    <th style={{ textAlign: 'right' }}>Оклад</th>
                    <th>Статус</th>
                    <th>Підстава</th>
                  </tr>
                </thead>
                <tbody>
                  {(historyQ.data ?? []).map((h) => (
                    <tr key={h.id}>
                      <td>
                        {new Date(h.valid_from).toLocaleDateString('uk-UA')}
                      </td>
                      <td>
                        {h.position_id ? posMap.get(h.position_id) ?? '—' : '—'}
                      </td>
                      <td>
                        {h.department_id ? depMap.get(h.department_id) ?? '—' : '—'}
                      </td>
                      <td style={{ textAlign: 'right' }}>{fmt2(h.salary_amount)}</td>
                      <td>{h.is_active ? '✓ працює' : '✗ звільнений'}</td>
                      <td style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                        {h.source_doc_type.replace('doc.', '')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </TchTable>
            )
          )}

          {cur === 'accruals' && <EmployeePlannedAccrualsSection employeeId={id!} />}

          {cur === 'documents' && (
            <EmployeeDocumentsSection
              employeeId={id!} posMap={posMap} depMap={depMap} show="kadr"
            />
          )}

          {cur === 'contracts' && (
            <EmployeeDocumentsSection
              employeeId={id!} posMap={posMap} depMap={depMap} show="civil"
              onCreateCivil={() => navigate(`/civil-law-contracts/new?employee=${id}`)}
            />
          )}

          {/* ч.883: блок «Коментарі та вкладення» (без номера). Вкладення
              картки (2026-08-19): підписані довідки з друку + будь-які файли. */}
          <div style={{ marginTop: '0.75rem' }}>
          <DocBlock title="Коментарі та вкладення">
            <div className="docblock__cols">
              <div className="field" style={{ margin: 0 }}>
                <label>Коментар</label>
                <input value={form.comment}
                       onChange={(e) => setForm({ ...form, comment: e.target.value })} />
              </div>
              <div>
                {form.organization_id && (
                  <AttachmentsPanel organizationId={form.organization_id}
                    documentType="ref.employees" documentId={id} />
                )}
              </div>
            </div>
          </DocBlock>
          </div>
        </>
        );
      })()}

      </DocLayout>
      {error && <div className="error">{error}</div>}
    </div>
  );
}

// Block 9 (2026-05-27): 2026 ПСП preview math (mirrors backend
// accounting_constants.compute_psp_*; рік 2026 = TSB 1664, threshold 4660).
// Якщо backend змінить значення — UI preview лишається довідковим;
// серверне обчислення під час нарахування ЗП — джерело істини.
const PSP_PREVIEW_2026 = {
  threshold: 4660,
  amount100: 1664,
  amount150: 2496,
};

function pspPreview(category: PspCategory | null, childrenCount: number | null):
  { amount: number; threshold: number } | null {
  if (category === null) return null;
  const n = childrenCount && childrenCount >= 1 ? childrenCount : 1;
  switch (category) {
    case 'GENERAL':
    case 'DISABILITY_GROUP_3':
    case 'CHERNOBYL_VICTIM_3_4':
      return { amount: PSP_PREVIEW_2026.amount100, threshold: PSP_PREVIEW_2026.threshold };
    case 'DISABILITY_GROUP_1_2':
    case 'CHERNOBYL_VICTIM_1_2':
    case 'COMBAT_VETERAN':
      return { amount: PSP_PREVIEW_2026.amount150, threshold: PSP_PREVIEW_2026.threshold };
    case 'MULTIPLE_CHILDREN':
      return {
        amount: PSP_PREVIEW_2026.amount100 * n,
        threshold: PSP_PREVIEW_2026.threshold * n,
      };
    case 'SINGLE_PARENT':
    case 'DISABLED_CHILD':
      return {
        amount: PSP_PREVIEW_2026.amount150 * n,
        threshold: PSP_PREVIEW_2026.threshold * n,
      };
    default:
      return null;
  }
}

interface PspFormSectionProps {
  pspCategory: PspCategory | null;
  childrenCount: number | null;
  onChange: (pspCategory: PspCategory | null, childrenCount: number | null) => void;
  disabled?: boolean;
}

export function PspFormSection({ pspCategory, childrenCount, onChange, disabled }: PspFormSectionProps) {
  const requiresChildren =
    pspCategory !== null && PSP_CHILDREN_CATEGORIES.includes(pspCategory);
  const preview = pspPreview(pspCategory, childrenCount);

  return (
    <div
      data-testid="psp-form-section"
      style={{
        marginTop: '1rem',
        padding: '0.75rem',
        background: 'var(--bg-soft-2)',
        borderRadius: 6,
        border: '1px solid var(--border)',
      }}
    >
      <label style={{ fontWeight: 600 }}>Податкова соціальна пільга (ПСП)</label>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: requiresChildren ? '2fr 1fr' : '1fr',
          gap: '0.5rem',
          marginTop: '0.4rem',
        }}
      >
        <div className="field">
          <label>Категорія</label>
          <select
            aria-label="ПСП категорія"
            disabled={disabled}
            value={pspCategory ?? ''}
            onChange={(e) => {
              const next = (e.target.value || null) as PspCategory | null;
              const nextRequiresChildren =
                next !== null && PSP_CHILDREN_CATEGORIES.includes(next);
              onChange(next, nextRequiresChildren ? (childrenCount ?? 1) : null);
            }}
          >
            {PSP_OPTIONS.map((opt) => (
              <option key={opt.value || 'none'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        {requiresChildren && (
          <div className="field">
            <label>Кількість дітей</label>
            <input
              aria-label="Кількість дітей"
              type="number"
              disabled={disabled}
              min={1}
              max={20}
              value={childrenCount ?? 1}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                onChange(pspCategory, Number.isFinite(n) && n >= 1 ? n : 1);
              }}
            />
          </div>
        )}
      </div>
      {preview !== null && (
        <div
          data-testid="psp-preview-info"
          style={{
            marginTop: '0.5rem',
            padding: '0.5rem 0.75rem',
            background: 'var(--primary-soft)',
            border: '1px solid var(--badge-blue-bd)',
            borderRadius: 4,
            color: '#1e3a8a',
            fontSize: '0.85rem',
          }}
        >
          ℹ ПСП застосовується якщо нарахування ≤{' '}
          <strong>{preview.threshold.toLocaleString('uk-UA')} грн</strong>.
          Розмір зменшення бази ПДФО: <strong>{preview.amount.toLocaleString('uk-UA')} грн</strong>{' '}
          (значення 2026 р., довідково).
        </div>
      )}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  if (e instanceof Error) return e.message;
  return String(e);
}
